import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {ValueOrderEntitlementsModel} from "@reva/shared/value-order";

/** Wie lange eine gelesene Antwort gilt (wie der Cache in reva-rest). */
export const ENTITLEMENTS_TTL_MS = 20 * 60 * 1000;
/** Wie lange eine gescheiterte Abfrage bzw. `UNAVAILABLE` gilt, bevor erneut gefragt wird. */
export const ENTITLEMENTS_RETRY_MS = 60 * 1000;

/**
 * Freischaltungen beim ValueAG-Auftraggeber der Lizenz des angemeldeten Benutzers – reva-rest beantwortet
 * `GET /value-orders/entitlements` ohne Parameter immer für diese Lizenz, daher genau ein Eintrag je Sitzung.
 */
export interface ValueOrderEntitlementsState {
    loading: boolean;
    /**
     * Letzte Antwort von reva-rest; bleibt während des Neuladens stehen, damit Button/Hinweis nicht flackern.
     * Eine gescheiterte Abfrage verwirft sie – dann gilt wie bei `UNAVAILABLE` das Beauftragen als möglich.
     */
    data: ValueOrderEntitlementsModel | null;
    /** Letzte Abfrage gescheitert – wird wie `UNAVAILABLE` behandelt (Beauftragen bleibt angeboten). */
    failed: boolean;
    /** Zeitpunkt (epoch ms) der letzten abgeschlossenen Abfrage; null = noch nie abgefragt. */
    loadedAt: number | null;
    /**
     * Laufende Nummer der Abfrage. Ergebnisse mit veralteter Nummer werden verworfen, damit eine vor dem Logout
     * gestartete Abfrage nicht in die Sitzung des nächsten Benutzers schreibt.
     */
    requestSeq: number;
}

const initialState: ValueOrderEntitlementsState = {
    loading: false,
    data: null,
    failed: false,
    loadedAt: null,
    requestSeq: 0,
};

export const valueOrderEntitlementsSlice = createSlice({
    name: "valueOrderEntitlements",
    initialState,
    reducers: {
        // requestSeq zählt weiter, damit noch laufende Abfragen nach dem Reset verworfen werden.
        resetValueOrderEntitlements: (state) => ({...initialState, requestSeq: state.requestSeq + 1}),
        setValueOrderEntitlementsLoading: (state) => {
            state.loading = true;
            state.requestSeq += 1;
        },
        setValueOrderEntitlementsLoaded: (
            state,
            action: PayloadAction<{requestSeq: number; data: ValueOrderEntitlementsModel; loadedAt: number}>,
        ) => {
            if (action.payload.requestSeq !== state.requestSeq) return;
            state.loading = false;
            state.data = action.payload.data;
            state.failed = false;
            state.loadedAt = action.payload.loadedAt;
        },
        setValueOrderEntitlementsFailed: (state, action: PayloadAction<{requestSeq: number; loadedAt: number}>) => {
            if (action.payload.requestSeq !== state.requestSeq) return;
            state.loading = false;
            state.data = null;
            state.failed = true;
            state.loadedAt = action.payload.loadedAt;
        },
    },
    selectors: {
        selectValueOrderEntitlementsState: (state) => state,
        selectValueOrderEntitlementsData: (state) => state.data,
    },
});

/**
 * Ob die Freischaltungen (erneut) abgefragt werden sollen: noch nie geladen oder abgelaufen. Gelesene Antworten
 * gelten {@link ENTITLEMENTS_TTL_MS}, Fehler und `UNAVAILABLE` nur {@link ENTITLEMENTS_RETRY_MS}.
 * @param state Zustand des Slices
 * @param now aktueller Zeitpunkt (epoch ms)
 */
export function isValueOrderEntitlementsStale(state: ValueOrderEntitlementsState, now: number): boolean {
    if (state.loading) return false;
    if (state.loadedAt === null) return true;
    const ttl = state.failed || state.data?.status === "UNAVAILABLE" ? ENTITLEMENTS_RETRY_MS : ENTITLEMENTS_TTL_MS;
    return now - state.loadedAt >= ttl;
}

export const {
    resetValueOrderEntitlements,
    setValueOrderEntitlementsLoading,
    setValueOrderEntitlementsLoaded,
    setValueOrderEntitlementsFailed,
} = valueOrderEntitlementsSlice.actions;

export const {selectValueOrderEntitlementsState, selectValueOrderEntitlementsData} =
    valueOrderEntitlementsSlice.selectors;

export default valueOrderEntitlementsSlice.reducer;
